import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Download, Plus } from 'lucide-react';
import { toast } from 'sonner';
import {
  Badge,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  Section,
  Select,
  Spinner,
  Textarea,
  type Tone,
} from '@zt/ui';
import { api, ApiError, IdempotencyKey } from '../lib/api';
import { HelpTip, PageFrame } from '../lib/help';
import { useI18n, useT } from '../lib/i18n';
import { bpsLabel, useMoney } from '../lib/money';
import { fieldError, useApiMutation } from '../lib/mutation';
import { usePermissions } from '../lib/session';
import type { Affiliate, AffiliateCommission, Discount } from '../lib/types';
import { percentToBps } from './pricing';

/**
 * Pazarlama → İş ortakları (F6-14, ADR-095): affiliate ve influencer kayıtları, kupon veya bağlantı kaynağıyla
 * sipariş eşleşmesi, komisyonlar (teslimden 14 gün sonra onaylanır) ve elle ödeme: CSV indir, "ödendi" işaretle.
 * Sistem kimseye ödeme yapmaz ve iş ortağına e-posta göndermez.
 */

type CommissionStatus = AffiliateCommission['status'];
const STATUSES: CommissionStatus[] = ['pending', 'approved', 'paid', 'cancelled'];
const statusTone: Record<CommissionStatus, Tone> = {
  pending: 'amber',
  approved: 'pine',
  paid: 'moss',
  cancelled: 'neutral',
};

export function AffiliatesPage() {
  const t = useT();
  const { can } = usePermissions();
  const [editing, setEditing] = useState<Affiliate | 'new' | null>(null);
  const list = useQuery({
    queryKey: ['affiliates'],
    queryFn: () => api.get<Affiliate[]>('/v1/admin/affiliates'),
  });
  return (
    <PageFrame
      helpKey="page.affiliates"
      actions={
        can('marketing.affiliate.manage') && (
          <div className="flex items-center gap-2">
            <Button variant="primary" onClick={() => setEditing('new')}>
              <Plus className="h-4 w-4" aria-hidden />
              {t('admin.affiliates.create')}
            </Button>
            <HelpTip k="affiliates.create" />
          </div>
        )
      }
    >
      <div className="space-y-6">
        <p className="max-w-[70ch] text-sm text-ink-2">{t('admin.affiliates.lead')}</p>
        {!list.data ? (
          <Spinner label={t('common.loading')} />
        ) : !list.data.length ? (
          <EmptyState
            title={t('admin.affiliates.emptyTitle')}
            description={t('admin.affiliates.emptyLead')}
          />
        ) : (
          <AffiliateTable rows={list.data} onEdit={setEditing} />
        )}
        {list.data && list.data.length > 0 && <CommissionsSection affiliates={list.data} />}
      </div>
      {editing && (
        <AffiliateDialog affiliate={editing === 'new' ? null : editing} onClose={() => setEditing(null)} />
      )}
    </PageFrame>
  );
}

function AffiliateTable({ rows, onEdit }: { rows: Affiliate[]; onEdit: (a: Affiliate) => void }) {
  const t = useT();
  const money = useMoney();
  const { can } = usePermissions();
  const total = (a: Affiliate, s: CommissionStatus) =>
    a.totals
      .filter((x) => x.status === s)
      .map((x) => money(x.commissionMinor, x.currency))
      .join(' + ') || '—';
  return (
    <div className="overflow-x-auto rounded-md border border-line">
      <table className="w-full min-w-[52rem] text-sm" aria-label={t('admin.affiliates.listLabel')}>
        <thead className="bg-surface-2 text-ink-2">
          <tr>
            <th className="px-3 py-2 text-start font-medium">{t('admin.affiliates.colName')}</th>
            <th className="px-3 py-2 text-start font-medium">{t('admin.affiliates.colMatch')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.affiliates.colRate')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.affiliates.colPending')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.affiliates.colApproved')}</th>
            <th className="px-3 py-2 text-end font-medium">{t('admin.affiliates.colPaid')}</th>
            <th className="px-3 py-2">
              <span className="sr-only">{t('admin.affiliates.colActions')}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr key={a.id} className="border-t border-line align-top">
              <td className="px-3 py-2">
                <span className="font-medium">{a.name}</span>{' '}
                <Badge tone="slate">{t(`admin.affiliates.kinds.${a.kind}`)}</Badge>
                {a.status === 'paused' && (
                  <>
                    {' '}
                    <Badge tone="amber">{t('admin.affiliates.paused')}</Badge>
                  </>
                )}
                {a.email && <span className="block text-ink-3">{a.email}</span>}
              </td>
              <td className="px-3 py-2">
                <span className="block font-mono text-[0.8125rem]">?utm_source={a.source}</span>
                {a.discountCode && (
                  <span className="block text-ink-2">
                    {t('admin.affiliates.coupon', { code: a.discountCode })}
                  </span>
                )}
              </td>
              <td className="px-3 py-2 text-end tabular">{bpsLabel(a.commissionBps)}</td>
              <td className="px-3 py-2 text-end tabular">{total(a, 'pending')}</td>
              <td className="px-3 py-2 text-end tabular">{total(a, 'approved')}</td>
              <td className="px-3 py-2 text-end tabular">{total(a, 'paid')}</td>
              <td className="px-3 py-2 text-end">
                {can('marketing.affiliate.manage') && (
                  <span className="inline-flex items-center gap-1">
                    <Button
                      size="sm"
                      onClick={() => onEdit(a)}
                      aria-label={t('admin.affiliates.editNamed', { name: a.name })}
                    >
                      {t('admin.affiliates.edit')}
                    </Button>
                    <HelpTip k="affiliates.edit" />
                  </span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AffiliateDialog({ affiliate, onClose }: { affiliate: Affiliate | null; onClose: () => void }) {
  const t = useT();
  const { can } = usePermissions();
  const [name, setName] = useState(affiliate?.name ?? '');
  const [kind, setKind] = useState<Affiliate['kind']>(affiliate?.kind ?? 'affiliate');
  // Maskeli e-posta (kişisel veri yetkisi yoksa) geri gönderilmez; alan yalnızca değiştirilince gönderilir.
  const [email, setEmail] = useState(affiliate?.email ?? '');
  const [source, setSource] = useState(affiliate?.source ?? '');
  const [discountId, setDiscountId] = useState(affiliate?.discountId ?? '');
  const [rate, setRate] = useState(affiliate ? bpsLabel(affiliate.commissionBps).replace('%', '') : '');
  const [status, setStatus] = useState<Affiliate['status']>(affiliate?.status ?? 'active');
  const [note, setNote] = useState(affiliate?.note ?? '');
  const coupons = useQuery({
    queryKey: ['discounts'],
    queryFn: () => api.get<Discount[]>('/v1/admin/discounts'),
  });
  const emailChanged = email !== (affiliate?.email ?? '');
  const body = {
    name: name.trim(),
    kind,
    ...(affiliate && !emailChanged ? {} : { email: email.trim() || null }),
    source: source.trim().toLowerCase(),
    discountId: discountId || null,
    commissionBps: percentToBps(rate) ?? -1,
    status,
    note: note.trim() || null,
  };
  const m = useApiMutation(
    () =>
      affiliate
        ? api.patch(`/v1/admin/affiliates/${affiliate.id}`, { ...body, version: affiliate.version })
        : api.post('/v1/admin/affiliates', body),
    {
      invalidate: [['affiliates']],
      success: affiliate ? t('admin.affiliates.updated') : t('admin.affiliates.created'),
      onSuccess: onClose,
      silentFieldErrors: true,
    },
  );
  const err = (f: string) => fieldError(m.error, f);
  const piiHidden = !!affiliate?.email && !can('customers.pii.read');
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={affiliate ? t('admin.affiliates.editTitle') : t('admin.affiliates.createTitle')}
      description={t('admin.affiliates.dialogLead')}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <span className="inline-flex items-center gap-1">
            <Button
              variant="primary"
              loading={m.isPending}
              disabled={name.trim().length < 2 || !source.trim() || !rate.trim()}
              onClick={() => m.mutate(undefined)}
            >
              {t('admin.affiliates.save')}
            </Button>
            <HelpTip k="affiliates.save" />
          </span>
        </>
      }
    >
      <div className="space-y-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('admin.affiliates.name')}
            required
            help={<HelpTip k="affiliates.name" />}
            error={err('name')}
          >
            {(p) => <Input {...p} value={name} maxLength={120} onChange={(e) => setName(e.target.value)} />}
          </Field>
          <Field label={t('admin.affiliates.kind')} help={<HelpTip k="affiliates.kind" />}>
            {(p) => (
              <Select {...p} value={kind} onChange={(e) => setKind(e.target.value as Affiliate['kind'])}>
                <option value="affiliate">{t('admin.affiliates.kinds.affiliate')}</option>
                <option value="influencer">{t('admin.affiliates.kinds.influencer')}</option>
              </Select>
            )}
          </Field>
        </div>
        <Field
          label={t('admin.affiliates.email')}
          help={<HelpTip k="affiliates.email" />}
          description={piiHidden ? t('admin.affiliates.emailMasked') : undefined}
          error={err('email')}
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              autoComplete="off"
              value={email}
              maxLength={200}
              onChange={(e) => setEmail(e.target.value)}
            />
          )}
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field
            label={t('admin.affiliates.source')}
            required
            help={<HelpTip k="affiliates.source" />}
            description={t('admin.affiliates.sourceHint', {
              source: source.trim().toLowerCase() || 'kedi-blog',
            })}
            error={err('source')}
          >
            {(p) => (
              <Input
                {...p}
                value={source}
                maxLength={40}
                className="font-mono"
                onChange={(e) => setSource(e.target.value)}
              />
            )}
          </Field>
          <Field
            label={t('admin.affiliates.rate')}
            required
            help={<HelpTip k="affiliates.rate" />}
            error={err('commissionBps')}
          >
            {(p) => (
              <Input
                {...p}
                inputMode="decimal"
                className="tabular"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
              />
            )}
          </Field>
        </div>
        <Field
          label={t('admin.affiliates.discount')}
          help={<HelpTip k="affiliates.discount" />}
          error={err('discountId')}
        >
          {(p) => (
            <Select {...p} value={discountId} onChange={(e) => setDiscountId(e.target.value)}>
              <option value="">{t('admin.affiliates.noDiscount')}</option>
              {(coupons.data ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.code} · {d.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        {affiliate && (
          <Field label={t('admin.affiliates.status')} help={<HelpTip k="affiliates.status" />}>
            {(p) => (
              <Select
                {...p}
                value={status}
                onChange={(e) => setStatus(e.target.value as Affiliate['status'])}
              >
                <option value="active">{t('admin.affiliates.statuses.active')}</option>
                <option value="paused">{t('admin.affiliates.statuses.paused')}</option>
              </Select>
            )}
          </Field>
        )}
        <Field label={t('admin.affiliates.note')} help={<HelpTip k="affiliates.note" />} error={err('note')}>
          {(p) => (
            <Textarea
              {...p}
              className="min-h-16"
              maxLength={1000}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------- Komisyonlar

function CommissionsSection({ affiliates }: { affiliates: Affiliate[] }) {
  const t = useT();
  const money = useMoney();
  const { formatDate } = useI18n();
  const { can } = usePermissions();
  const [affiliateId, setAffiliateId] = useState('');
  const [status, setStatus] = useState<'' | CommissionStatus>('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [paying, setPaying] = useState(false);
  const [busy, setBusy] = useState(false);
  const params = new URLSearchParams();
  if (affiliateId) params.set('affiliateId', affiliateId);
  if (status) params.set('status', status);
  const list = useQuery({
    queryKey: ['affiliate-commissions', affiliateId, status],
    queryFn: () => api.get<AffiliateCommission[]>(`/v1/admin/affiliate-commissions?${params.toString()}`),
  });
  const rows = list.data ?? [];
  const approvedIds = rows.filter((c) => c.status === 'approved').map((c) => c.id);
  const chosen = approvedIds.filter((id) => selected.has(id));
  const manage = can('marketing.affiliate.manage');
  const toggle = (id: string, on: boolean) =>
    setSelected((s) => {
      const n = new Set(s);
      if (on) n.add(id);
      else n.delete(id);
      return n;
    });
  const download = async () => {
    setBusy(true);
    try {
      const { blob, fileName } = await api.blob(
        `/v1/admin/affiliate-commissions/export?${params.toString()}`,
      );
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = fileName ?? 'is-ortagi-komisyonlari.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (e) {
      if (!(e instanceof ApiError && e.code === 'step_up_required'))
        toast.error(e instanceof ApiError ? e.title : String(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Section
      title={t('admin.affiliates.commissions')}
      description={t('admin.affiliates.commissionsLead')}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <span className="inline-flex items-center gap-1">
            <Button size="sm" onClick={() => void download()} loading={busy}>
              <Download className="h-4 w-4" aria-hidden />
              {t('admin.affiliates.export')}
            </Button>
            <HelpTip k="commissions.export" />
          </span>
          {manage && (
            <span className="inline-flex items-center gap-1">
              <Button size="sm" variant="primary" disabled={!chosen.length} onClick={() => setPaying(true)}>
                {t('admin.affiliates.markPaid', { count: chosen.length })}
              </Button>
              <HelpTip k="commissions.mark_paid" />
            </span>
          )}
        </div>
      }
    >
      <div className="mb-3 grid max-w-xl gap-3 sm:grid-cols-2">
        <Field
          label={t('admin.affiliates.filterAffiliate')}
          help={<HelpTip k="commissions.filter_affiliate" />}
        >
          {(p) => (
            <Select {...p} value={affiliateId} onChange={(e) => setAffiliateId(e.target.value)}>
              <option value="">{t('common.all')}</option>
              {affiliates.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label={t('admin.affiliates.filterStatus')} help={<HelpTip k="commissions.filter_status" />}>
          {(p) => (
            <Select {...p} value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
              <option value="">{t('common.all')}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`admin.affiliates.commissionStatuses.${s}`)}
                </option>
              ))}
            </Select>
          )}
        </Field>
      </div>
      {!list.data ? (
        <Spinner label={t('common.loading')} />
      ) : !rows.length ? (
        <EmptyState
          title={t('admin.affiliates.noCommissions')}
          description={t('admin.affiliates.noCommissionsLead')}
        />
      ) : (
        <div className="overflow-x-auto rounded-md border border-line">
          <table className="w-full min-w-[52rem] text-sm" aria-label={t('admin.affiliates.commissions')}>
            <thead className="bg-surface-2 text-ink-2">
              <tr>
                {manage && (
                  <th className="w-10 px-3 py-2">
                    <span className="inline-flex items-center gap-1">
                      <input
                        type="checkbox"
                        className="h-4 w-4 accent-[var(--zt-pine)]"
                        aria-label={t('admin.affiliates.selectAll')}
                        disabled={!approvedIds.length}
                        checked={approvedIds.length > 0 && chosen.length === approvedIds.length}
                        onChange={(e) => setSelected(e.target.checked ? new Set(approvedIds) : new Set())}
                      />
                      <HelpTip k="commissions.select" />
                    </span>
                  </th>
                )}
                <th className="px-3 py-2 text-start font-medium">{t('admin.affiliates.colOrder')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.affiliates.colAffiliate')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.affiliates.colBase')}</th>
                <th className="px-3 py-2 text-end font-medium">{t('admin.affiliates.colCommission')}</th>
                <th className="px-3 py-2 text-start font-medium">{t('admin.affiliates.colStatus')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((c) => (
                <tr key={c.id} className="border-t border-line align-top">
                  {manage && (
                    <td className="px-3 py-2">
                      {c.status === 'approved' && (
                        <input
                          type="checkbox"
                          className="h-4 w-4 accent-[var(--zt-pine)]"
                          aria-label={t('admin.affiliates.selectOne', { order: c.orderNumber })}
                          checked={selected.has(c.id)}
                          onChange={(e) => toggle(c.id, e.target.checked)}
                        />
                      )}
                    </td>
                  )}
                  <td className="px-3 py-2">
                    <Link
                      to="/siparisler/$id"
                      params={{ id: c.orderId }}
                      className="text-pine underline underline-offset-2 hover:no-underline"
                    >
                      {c.orderNumber}
                    </Link>
                    <span className="block text-ink-3">{c.placedAt ? formatDate(c.placedAt) : '—'}</span>
                  </td>
                  <td className="px-3 py-2">
                    {c.affiliateName}
                    <span className="block text-ink-3">{t(`admin.affiliates.sources.${c.source}`)}</span>
                  </td>
                  <td className="px-3 py-2 text-end tabular">{money(c.baseMinor, c.currency)}</td>
                  <td className="px-3 py-2 text-end tabular">
                    {money(c.commissionMinor, c.currency)}
                    <span className="block text-ink-3">{bpsLabel(c.rateBps)}</span>
                  </td>
                  <td className="px-3 py-2">
                    <Badge tone={statusTone[c.status]}>
                      {t(`admin.affiliates.commissionStatuses.${c.status}`)}
                    </Badge>
                    {c.status === 'paid' && c.paidAt && (
                      <span className="block text-ink-3">{formatDate(c.paidAt)}</span>
                    )}
                    {c.paidNote && <span className="block text-ink-3">{c.paidNote}</span>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {paying && (
        <MarkPaidDialog
          ids={chosen}
          totals={rows.filter((c) => chosen.includes(c.id))}
          onClose={() => setPaying(false)}
          onDone={() => {
            setPaying(false);
            setSelected(new Set());
          }}
        />
      )}
    </Section>
  );
}

function MarkPaidDialog({
  ids,
  totals,
  onClose,
  onDone,
}: {
  ids: string[];
  totals: AffiliateCommission[];
  onClose: () => void;
  onDone: () => void;
}) {
  const t = useT();
  const money = useMoney();
  const [note, setNote] = useState('');
  const [idem] = useState(() => new IdempotencyKey());
  const sums = new Map<string, number>();
  for (const c of totals) sums.set(c.currency, (sums.get(c.currency) ?? 0) + c.commissionMinor);
  const m = useApiMutation(
    () =>
      api.post<{ paid: number }>(
        '/v1/admin/affiliate-commissions/mark-paid',
        { ids, note: note.trim() },
        { idempotencyKey: idem },
      ),
    {
      invalidate: [['affiliate-commissions'], ['affiliates']],
      success: (r) => t('admin.affiliates.markedPaid', { count: r.paid }),
      onSuccess: onDone,
      silentFieldErrors: true,
    },
  );
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title={t('admin.affiliates.markPaidTitle')}
      description={t('admin.affiliates.markPaidLead', {
        count: ids.length,
        total: [...sums].map(([cur, v]) => money(v, cur)).join(' + '),
      })}
      closeLabel={t('common.close')}
      footer={
        <>
          <Button onClick={onClose}>{t('common.cancel')}</Button>
          <Button
            variant="primary"
            loading={m.isPending}
            disabled={note.trim().length < 3}
            onClick={() => m.mutate(undefined)}
          >
            {t('admin.affiliates.markPaidConfirm')}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <Field
          label={t('admin.affiliates.paidNote')}
          required
          help={<HelpTip k="commissions.paid_note" />}
          description={t('admin.affiliates.paidNoteHint')}
          error={fieldError(m.error, 'note')}
        >
          {(p) => (
            <Textarea
              {...p}
              className="min-h-16"
              maxLength={500}
              value={note}
              onChange={(e) => setNote(e.target.value)}
            />
          )}
        </Field>
        {m.error && !m.error.errors.length && (
          <p role="alert" className="text-sm text-rose">
            {m.error.title}
          </p>
        )}
      </div>
    </Dialog>
  );
}
